// BUILD-042 (Oct 24) - guided initial HR setup: the pure screen logic (no DOM, no React, no network). Tested under plain Node (setupModel.test.mjs).
//
// Authority: docs/superpowers/decisions/2026-10-24-build-042-guided-intake-admin-layout-local.md (+ four-image flow clarification).
// The SERVER owns the facts: where the setup is (progress.step), HR's explicit decisions, and the real saved assignments. This module only turns what
// the server returned (or failed to return) into what a rail, a review row or a redirect may truthfully say. Nothing here is an authorization decision.
import type { HrSetupProgress, HrSetupStep, HrSetupStepStatus, HrSetupSummary } from '../../../api/client';

export interface StepDef { key: HrSetupStep; number: number; label: string }
export const SETUP_STEPS: readonly StepDef[] = [
  { key: 'details', number: 1, label: 'Employee details' },
  { key: 'benefits', number: 2, label: 'Benefits' },
  { key: 'checklist', number: 3, label: 'Onboarding checklist' },
  { key: 'documents', number: 4, label: 'Documents' },
  { key: 'equipment', number: 5, label: 'Equipment' },
  { key: 'review', number: 6, label: 'Review' },
] as const;

export function stepIndex(step: HrSetupStep): number {
  return SETUP_STEPS.findIndex((s) => s.key === step);
}
export function stepDef(step: HrSetupStep): StepDef {
  return SETUP_STEPS[Math.max(0, stepIndex(step))];
}
export function parseStepParam(raw: string | null | undefined): HrSetupStep | null {
  return SETUP_STEPS.find((s) => s.key === raw)?.key ?? null;
}
export function nextStepOf(step: HrSetupStep): HrSetupStep {
  return SETUP_STEPS[Math.min(SETUP_STEPS.length - 1, stepIndex(step) + 1)].key;
}
export function previousStepOf(step: HrSetupStep): HrSetupStep | null {
  const i = stepIndex(step);
  return i > 0 ? SETUP_STEPS[i - 1].key : null;
}

/**
 * Which step the screen opens on (cycle 12, flexible navigation). A ?step= deep link to ANY of the six steps is valid: HR may visit steps out of order, and
 * Review may be opened at any time (Finish stays blocked until the server says otherwise). Without a link the screen opens at the server's recorded step,
 * which remains the resume point; opening a step never moves it.
 */
export function resolveStep(requested: HrSetupStep | null, serverStep: HrSetupStep): HrSetupStep {
  return requested ?? serverStep;
}
/** The step to store on "Save & continue": normally the next one, but never LOWER than where the server already is (re-saving an earlier step must not forget later progress). */
export function stepToStore(from: HrSetupStep, serverStep: HrSetupStep): HrSetupStep {
  const next = nextStepOf(from);
  return stepIndex(next) >= stepIndex(serverStep) ? next : serverStep;
}

export function setupPath(employeeId: string, step?: HrSetupStep | null, from?: 'review' | null): string {
  const q = new URLSearchParams();
  if (step) q.set('step', step);
  if (from) q.set('from', from);
  const s = q.toString();
  return `/hr/admin/employees/${encodeURIComponent(employeeId)}/setup${s ? `?${s}` : ''}`;
}
export function employeePath(employeeId: string, extra = ''): string {
  return `/hr/admin/employees/${encodeURIComponent(employeeId)}${extra}`;
}
export const QUEUE_PATH = '/hr/admin?tab=awaiting';

/**
 * Opening the normal employee page for a person whose setup is unfinished goes to setup - unless the URL carries an explicit deep link (?section= or ?item=),
 * which opens the normal page with a banner instead. Legacy people (progress null) and finished setups never redirect.
 */
export function shouldRedirectToSetup(progressStatus: HrSetupProgress['status'] | null | undefined, search: string): boolean {
  if (progressStatus !== 'in_progress') return false;
  const q = new URLSearchParams(search);
  return !q.has('section') && !q.has('item');
}
export function hasExplicitDeepLink(search: string): boolean {
  const q = new URLSearchParams(search);
  return q.has('section') || q.has('item');
}

// ─── Per-step standing (cycle 12) ───────────────────────────────────────────────────────────────────────────────────────────────
// The SERVER derives `summary.steps` (unvisited / needs_attention / configured / not_needed; 'deferred' is legacy tolerance only) from saved authoritative data + HR's flags.
// This module shows what the server says. Only when an older server omits `steps` does it derive the same standing from decisions + flags + the real
// summary data (same rules), so the rail and the Review never invent a completed state.
export type AreaStep = Exclude<HrSetupStep, 'review'>;
export const AREA_STEPS: readonly AreaStep[] = ['details', 'benefits', 'checklist', 'documents', 'equipment'] as const;
type Area = 'benefits' | 'checklist' | 'documents' | 'equipment';
export interface StepStanding { status: HrSetupStepStatus; reason: string | null; /** The caller cannot read this area; it can never be resolved from this account. */ unavailable: boolean }

/** Resolved = HR's work on the step is finished (a blue check). */
export function isResolved(status: HrSetupStepStatus): boolean {
  return status === 'configured' || status === 'not_needed';
}

export interface BenefitCounts { applicable: number; eligible: number; notEligible: number; needDecision: number }
/** The benefit plans HR must decide, from the real summary. Never reports a plan as decided that the server has not recorded as eligible-with-date / not eligible. */
export function benefitCounts(b: Extract<HrSetupSummary['benefits'], { state: 'available' }>): BenefitCounts {
  const items = b.items ?? [];
  const applicable = b.applicableCount ?? items.length;
  const fromItems = items.length === applicable;
  const eligible = fromItems ? items.filter((i) => i.status === 'eligible' && !!i.eligibilityDate).length : b.eligibleCount;
  const notEligible = fromItems ? items.filter((i) => i.status === 'not_eligible').length : b.notEligibleCount;
  const decided = b.decidedCount ?? (eligible + notEligible);
  return { applicable, eligible, notEligible, needDecision: Math.max(0, applicable - decided) };
}

function areaUnavailable(step: AreaStep, summary: HrSetupSummary | null): boolean {
  return !!summary && step !== 'details' && summary[step as Area].state === 'unavailable';
}

/** Fallback derivation (older server without `summary.steps`): mirrors the contract rules using only data the summary really carries. */
function deriveStanding(step: AreaStep, progress: HrSetupProgress | null, summary: HrSetupSummary): StepStanding {
  const decisions = progress?.decisions ?? {};
  const flags = progress?.flags?.[step] ?? {};
  const unavailable = areaUnavailable(step, summary);
  let resolved: 'configured' | 'not_needed' | null = null;
  let reason: string | null = null;
  let hasData = false; // real saved data exists (a conflicting decision, a draft, a partly decided plan list): never shown as blank/unvisited
  switch (step) {
    case 'details':
      if (decisions.details?.saved && summary.details.hireDate) resolved = 'configured'; else reason = 'Employee details have not been saved and confirmed.';
      break;
    case 'benefits': {
      const b = summary.benefits;
      if (b.state === 'unavailable') { reason = 'Benefits can’t be shown with your permissions.'; break; }
      const c = benefitCounts(b);
      hasData = c.eligible + c.notEligible > 0 || !!decisions.benefits?.reviewed;
      if (decisions.benefits?.reviewed && c.needDecision === 0) resolved = 'configured';
      else reason = c.needDecision > 0 ? `${c.needDecision} benefit plan${c.needDecision === 1 ? '' : 's'} need${c.needDecision === 1 ? 's' : ''} a decision.` : 'Benefits have not been marked as reviewed.';
      break;
    }
    case 'checklist': {
      const c = summary.checklist;
      if (c.state === 'unavailable') { reason = 'Onboarding checklist can’t be shown with your permissions.'; break; }
      const decided = decisions.checklist?.decision === 'not_applicable';
      if (decided && c.onboardingCount > 0) { hasData = true; reason = 'A checklist was assigned after “no onboarding checklist needed” was recorded.'; }
      else if (decided) resolved = 'not_needed';
      else if (c.onboardingCount > 0) resolved = 'configured';
      else reason = 'Assign an onboarding checklist, or confirm none is needed.';
      break;
    }
    case 'documents': {
      const d = summary.documents;
      if (d.state === 'unavailable') { reason = 'Documents can’t be shown with your permissions.'; break; }
      const decided = decisions.documents?.decision === 'not_applicable';
      if (decided && d.liveCount > 0) { hasData = true; reason = 'Forms were assigned after “no documents needed” was recorded.'; }
      else if (decided) resolved = 'not_needed';
      else if (d.liveCount > 0) resolved = 'configured';
      else reason = 'Assign the forms this employee needs, or confirm none are needed.';
      break;
    }
    case 'equipment': {
      const e = summary.equipment;
      if (e.state === 'unavailable') { reason = 'Equipment can’t be shown with your permissions.'; break; }
      const issued = e.checkouts.filter((c) => c.status === 'generated').length;
      const drafts = e.checkouts.length - issued;
      const decided = decisions.equipment?.decision === 'no_equipment_needed';
      hasData = drafts > 0;
      if (decided && e.checkouts.length > 0) { hasData = true; reason = 'Equipment was started after “no equipment needed” was recorded.'; }
      else if (decided) resolved = 'not_needed';
      else if (issued > 0) resolved = 'configured';
      else reason = drafts > 0 ? 'An equipment draft is saved but nothing is issued yet.' : 'Issue equipment, or confirm none is needed.';
      break;
    }
  }
  if (resolved) return { status: resolved, reason: null, unavailable };
  const passed = !!progress && stepIndex(step) < stepIndex(progress.step);
  // A legacy stored `deferred` flag (cycle 12 "Come back later", now removed) reads as a visited, unresolved step.
  const status: HrSetupStepStatus = (flags.deferred || flags.visited || passed || hasData) ? 'needs_attention' : 'unvisited';
  return { status, reason, unavailable };
}

/** Where every step stands. Server `summary.steps` wins; a finished setup shows everything configured. */
export function stepStandings(progress: HrSetupProgress | null, summary: HrSetupSummary | null): Record<AreaStep, StepStanding> {
  const out = {} as Record<AreaStep, StepStanding>;
  for (const s of AREA_STEPS) {
    if (progress?.status === 'finished') { out[s] = { status: 'configured', reason: null, unavailable: false }; continue; }
    if (!summary) { out[s] = { status: 'unvisited', reason: null, unavailable: false }; continue; }
    const server = summary.steps?.[s];
    // Any legacy 'deferred' status from a server is shown as needs_attention: there is no "Come back later" in the product any more.
    out[s] = server ? { status: server.status === 'deferred' ? 'needs_attention' : server.status, reason: server.reason ?? null, unavailable: areaUnavailable(s, summary) } : deriveStanding(s, progress, summary);
  }
  return out;
}

/** The steps that block Finish, with the reason. Server `summary.blockers` wins; otherwise derived from the standings. */
export function setupBlockers(progress: HrSetupProgress | null, summary: HrSetupSummary | null): Array<{ step: AreaStep; status: HrSetupStepStatus; reason: string }> {
  if (summary?.blockers) return summary.blockers.map((b) => ({ step: b.step, status: b.status === 'deferred' ? 'needs_attention' as const : b.status, reason: b.reason }));
  const st = stepStandings(progress, summary);
  return AREA_STEPS.filter((s) => !isResolved(st[s].status)).map((s) => ({ step: s, status: st[s].status, reason: st[s].reason ?? 'Still to do.' }));
}

/** Finish is possible only when the server says so (or, for an older server, when every step is resolved and every area is readable). */
export function canFinishSetup(progress: HrSetupProgress | null, summary: HrSetupSummary | null): boolean {
  if (!progress || progress.status !== 'in_progress' || !summary) return false;
  if (unavailableAreas(summary).length > 0) return false;
  if (typeof summary.canFinish === 'boolean') return summary.canFinish;
  return setupBlockers(progress, summary).length === 0;
}

export const STEP_LABEL: Record<AreaStep, string> = {
  details: 'Employee details', benefits: 'Benefits', checklist: 'Onboarding checklist', documents: 'Documents', equipment: 'Equipment',
};

/** The words for a status. Text always accompanies the colour/icon. `visible` is null for the neutral/finished states that need no extra words. */
export function statusWords(status: HrSetupStepStatus): { visible: string | null; spoken: string } {
  switch (status) {
    case 'unvisited': return { visible: null, spoken: 'not visited yet' };
    case 'deferred': // legacy tolerance only: read as needs_attention
    case 'needs_attention': return { visible: 'Needs attention', spoken: 'needs attention' };
    case 'configured': return { visible: null, spoken: 'configured' };
    case 'not_needed': return { visible: 'Not needed', spoken: 'not needed' };
  }
}
/** The status chip on a Review row. */
export function chipWords(status: HrSetupStepStatus): string {
  switch (status) {
    case 'configured': return 'Setup reviewed';
    case 'not_needed': return 'Not needed';
    case 'deferred': // legacy tolerance only: read as needs_attention
    case 'needs_attention': return 'Needs attention';
    case 'unvisited': return 'Not visited yet';
  }
}

/** Every area the caller cannot read, with the server's own words. A setup cannot be finished while any of these exists. */
export function unavailableAreas(summary: HrSetupSummary | null): Array<{ step: Area; label: string; reason: string; capability: string | null }> {
  if (!summary) return [];
  const labels: Record<Area, string> = { benefits: 'Benefits', checklist: 'Onboarding checklist', documents: 'Documents', equipment: 'Equipment' };
  const out: Array<{ step: Area; label: string; reason: string; capability: string | null }> = [];
  for (const a of ['benefits', 'checklist', 'documents', 'equipment'] as Area[]) {
    const s = summary[a];
    if (s.state === 'unavailable') out.push({ step: a, label: labels[a], reason: s.reason, capability: s.capability });
  }
  return out;
}

export const CANT_SHOW_TEXT = 'Can’t be shown with your permissions';
export function unavailableText(reason: string, capability: string | null): string {
  return capability ? `${CANT_SHOW_TEXT}: it ${reason}.` : `${CANT_SHOW_TEXT}: ${reason}.`;
}

// ─── Review rows (the real saved state, never wizard memory) ───────────────────────────────────────────────────────────────────
export interface ReviewRow { step: AreaStep; title: string; line: string; note?: string; status: HrSetupStepStatus; chip: string; unavailable: boolean }

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}
const WAITING_DOC = new Set(['awaiting_return', 'needs_correction']);

export function reviewRows(
  progress: HrSetupProgress | null, summary: HrSetupSummary,
  fmt: { date: (iso: string | null) => string; department: (id: string | null) => string | null }
): ReviewRow[] {
  const decisions = progress?.decisions ?? {};
  const st = stepStandings(progress, summary);
  const mk = (step: AreaStep, line: string, note?: string): ReviewRow => ({ step, title: STEP_LABEL[step], line, note, status: st[step].status, chip: chipWords(st[step].status), unavailable: st[step].unavailable });
  const rows: ReviewRow[] = [];

  const d = summary.details;
  const dept = fmt.department(d.departmentId);
  const parts = [`Hire date ${fmt.date(d.hireDate)}`];
  if (d.title) parts.push(d.title);
  if (dept) parts.push(dept);
  if (!decisions.details?.saved) parts.push('not saved yet');
  const missing: string[] = [];
  if (!d.departmentId) missing.push('Department');
  if (!d.managerEmployeeId) missing.push('manager');
  rows.push(mk('details', parts.join(' · '), missing.length ? `${missing.join(' and ')} not assigned (optional).` : undefined));

  const b = summary.benefits;
  if (b.state === 'unavailable') rows.push(mk('benefits', unavailableText(b.reason, b.capability)));
  else {
    const c = benefitCounts(b);
    rows.push(mk('benefits', c.applicable === 0
      ? 'No benefit plans to decide right now'
      : `${c.eligible} eligible · ${c.notEligible} not eligible · ${c.needDecision === 1 ? '1 needs a decision' : `${c.needDecision} need a decision`}`));
  }

  const c = summary.checklist;
  if (c.state === 'unavailable') rows.push(mk('checklist', unavailableText(c.reason, c.capability)));
  else {
    const onb = c.assignments.filter((a) => a.processKind === 'onboarding');
    const pending = onb.reduce((n, a) => n + Math.max(0, a.taskCount - a.completedCount), 0);
    rows.push(mk('checklist', onb.length > 0
      ? `${onb.map((a) => a.templateName).join(', ')} assigned · ${plural(pending, 'task')} pending`
      : decisions.checklist?.decision === 'not_applicable' ? 'HR confirmed no onboarding checklist is needed' : 'Nothing assigned yet'));
  }

  const doc = summary.documents;
  if (doc.state === 'unavailable') rows.push(mk('documents', unavailableText(doc.reason, doc.capability)));
  else if (doc.liveCount > 0) {
    const waiting = doc.assignments.filter((a) => WAITING_DOC.has(a.status)).length;
    const review = doc.assignments.filter((a) => a.status === 'awaiting_hr_review').length;
    const tail = [waiting > 0 ? 'Waiting on employee' : null, review > 0 ? `${review} need${review === 1 ? 's' : ''} HR review` : null].filter(Boolean);
    rows.push(mk('documents', `${plural(doc.liveCount, 'form')} assigned${tail.length ? ` · ${tail.join(' · ')}` : ' · All reviewed'}`));
  } else rows.push(mk('documents', decisions.documents?.decision === 'not_applicable' ? 'HR confirmed no forms are needed' : 'Nothing assigned yet'));

  const eq = summary.equipment;
  if (eq.state === 'unavailable') rows.push(mk('equipment', unavailableText(eq.reason, eq.capability)));
  else {
    const issued = eq.checkouts.filter((x) => x.status === 'generated');
    const drafts = eq.checkouts.filter((x) => x.status === 'draft');
    const items = issued.reduce((n, x) => n + x.itemCount, 0);
    rows.push(mk('equipment', issued.length > 0
      ? `${plural(issued.length, 'equipment checkout')} issued · ${plural(items, 'item')}`
      : drafts.length > 0 ? 'Draft saved, not issued yet'
      : decisions.equipment?.decision === 'no_equipment_needed' ? 'HR confirmed no equipment needed' : 'Nothing issued yet'));
  }
  return rows;
}

// ─── Failures, in plain words ───────────────────────────────────────────────────────────────────────────────────────────────────
export interface SetupMissing { step: HrSetupStep; reason: string; capability?: string | null }
export interface SetupFailure { message: string; missing: SetupMissing[]; kind: 'incomplete' | 'denied' | 'conflict' | 'other' }
export function setupFailure(e: unknown, fallback: string): SetupFailure {
  const err = e as { status?: number; code?: string; message?: string; body?: { missing?: SetupMissing[] } } | null | undefined;
  const missing = Array.isArray(err?.body?.missing) ? err!.body!.missing! : [];
  const message = (typeof err?.message === 'string' && err.message.trim()) || fallback;
  if (err?.status === 409 && (err.code === 'SETUP_INCOMPLETE' || missing.length > 0)) return { message, missing, kind: 'incomplete' };
  if (err?.status === 403) return { message, missing, kind: 'denied' };
  if (err?.status === 409) return { message, missing, kind: 'conflict' };
  return { message, missing, kind: 'other' };
}

/** The newest term to show for a person: the current one, else (a future hire date) the soonest-dated group's newest. Input is newest-first within a date, as the server returns it. */
export function displayTerm<T extends { effectiveDate: string }>(termHistory: T[], today: string): T | null {
  const sorted = [...termHistory].sort((a, b) => (a.effectiveDate < b.effectiveDate ? 1 : a.effectiveDate > b.effectiveDate ? -1 : 0));
  const past = sorted.find((t) => t.effectiveDate <= today);
  if (past) return past;
  if (!sorted.length) return null;
  const soonest = sorted[sorted.length - 1].effectiveDate;
  return sorted.find((t) => t.effectiveDate === soonest) ?? null;
}

/** Local Phoenix "today" as YYYY-MM-DD (the company's calendar). */
export function todayPhoenix(now = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Phoenix', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

// ─── Job-detail save payload (Oct 24 closeout, finding 2) ──────────────────────────────────────────────────────────────────────
// The employment-change route is a PARTIAL update: a key that is ABSENT keeps the value on record (the server carries it forward from the term in
// effect at the change's effective date); a key sent as null is a deliberate clear. So the screen sends ONLY the fields the person changed, null only
// when a field was emptied on purpose, and never a whole snapshot (a stale snapshot would overwrite entries saved a moment earlier).
// Classification is deliberately NOT here: there is no accepted value set for it, so no screen writes it (it stays a read-only display).
export type JobField = 'title' | 'departmentId' | 'managerEmployeeId';
export interface JobBaseline { title: string | null; departmentId: string | null; managerEmployeeId: string | null }
export interface JobValues { title: string; departmentId: string; managerEmployeeId: string }

export function changedJobFields(baseline: JobBaseline, values: JobValues): JobField[] {
  const out: JobField[] = [];
  if (values.title.trim() !== (baseline.title ?? '').trim()) out.push('title');
  if (values.departmentId !== (baseline.departmentId ?? '')) out.push('departmentId');
  if (values.managerEmployeeId !== (baseline.managerEmployeeId ?? '')) out.push('managerEmployeeId');
  return out;
}

/** The body for POST .../employment-changes, or null when nothing changed (nothing is sent, nothing is written). */
export function jobChangeBody(effectiveDate: string, baseline: JobBaseline, values: JobValues, reason?: string): Record<string, unknown> | null {
  const changed = changedJobFields(baseline, values);
  if (changed.length === 0) return null;
  const body: Record<string, unknown> = { effectiveDate };
  for (const f of changed) body[f] = f === 'title' ? (values.title.trim() || null) : (values[f] || null);
  if (reason && reason.trim()) body.reason = reason.trim();
  return body;
}

/** A later edit of a person whose hire date is still in the future starts on the hire date (before it there is no employment term to carry forward). */
export function defaultEffectiveDate(hireDate: string | null | undefined, today: string): string {
  return hireDate && hireDate > today ? hireDate : today;
}
